import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/notifications/styles.css";

import { LoadingOverlay, MantineProvider, rem } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { SWRConfig } from "swr";
import { SWRDevTools } from "swr-devtools";
import httpClient from "@/config/httpClient";

import "./config/httpClientConfig";
import "./config/dayjsConfig";

import ErrorBoundary from "./components/common/ErrorBoundary/ErrorBoundary";
import ErrorServer from "./components/common/ErrorBoundary/ErrorServer";
import { userStore } from "./mobx/rootStore";
import { RoutesGroup } from "./routes";
import { isPublicPath } from "./routesList";
import { swrFetcher } from "./utils/swr/fetcher";

import "driver.js/dist/driver.css";
import { notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { logout } from "@/utils/auth/logout";

import "./index.css";
import BadgeClasses from "./styles/components/Badge.module.css";

const DEFAULT_FONT_FAMILY = "FunnelSans, ui-sans-serif, sans-serif";

export default function App() {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: true,
						staleTime: 5_000,
						retry: 1,
					},
				},
			}),
	);

	const [isInitializing, setIsInitializing] = useState(true);
	const [initError, setInitError] = useState(null);

	useEffect(() => {
		(async () => {
			if (!userStore?.token) {
				if (isPublicPath(window.location.pathname)) {
					setIsInitializing(false);
					return;
				}

				logout({});
				return;
			}

			httpClient.headers.Authorization = `Bearer ${userStore?.token}`;
			try {
				const result = await userStore.init();
				if (result?.error === "server") {
					setInitError("server");
					return;
				}
				if (result?.error === "auth") {
					notifications.show({
						title: "Sesión expirada",
						message: "Necesitas iniciar sesión",
						color: "orange",
					});
					logout({});
					return;
				}
			} catch (error) {
				console.error("Error initializing user:", error);
				setInitError("server");
				return;
			}
			setIsInitializing(false);
		})();
	}, []);

	return (
		<MantineProvider
			defaultColorScheme="dark"
			withGlobalClasses
			/* cssVariablesResolver={(theme) => {
				return {
					variables: {},
				};
			}} */

			// https://github.com/mantinedev/mantine/blob/2f91229da10d30a205ade277161bc8829740bd93/packages/%40mantine/core/src/core/MantineProvider/default-theme.ts
			theme={{
				fontFamily: DEFAULT_FONT_FAMILY,

				scale: 1, // https://mantine.dev/styles/rem/#rem-units-scaling

				primaryColor: "indigo",

				fontSizes: {
					n: rem(6),
					xxxs: rem(8),
					xxs: rem(10),
					xs: rem(12),
					sm: rem(14),
					md: rem(16),
					lg: rem(18),
					xl: rem(20),
					xxl: rem(22),
					xxxl: rem(24),
					g: rem(26),
				},

				spacing: {
					n: rem(2),
					xxxs: rem(4),
					xxs: rem(8),
					xs: rem(10),
					sm: rem(12),
					md: rem(16),
					lg: rem(20),
					xl: rem(32),
					xxl: rem(36),
					xxxl: rem(40),
				},

				breakpoints: {
					xs: "36em",
					sm: "48em",
					md: "62em",
					lg: "75em",
					xl: "88em",
				},

				components: {
					LoadingOverlay: {
						defaultProps: {
							overlayProps: { blur: 4, backgroundOpacity: 0 },
							loaderProps: { type: "bars" },
						},
					},
					Badge: {
						classNames: {
							root: BadgeClasses.root,
						},
					},
				},
			}}
		>
			<ErrorBoundary>
				<Notifications />
				<ModalsProvider>
					<QueryClientProvider client={queryClient}>
						<SWRConfig value={{ fetcher: swrFetcher }}>
							<BrowserRouter>
								{initError === "server" ? (
									<ErrorServer />
								) : isInitializing ? (
									<LoadingOverlay
										visible={true}
										overlayProps={{
											radius: "sm",
											blur: 1,
										}}
										zIndex={1000}
									/>
								) : (
									<Suspense
										fallback={
											<LoadingOverlay
												visible={true}
												overlayProps={{
													radius: "sm",
													blur: 1,
												}}
												zIndex={1000}
											/>
										}
									>
										<SWRDevTools>
											<RoutesGroup />
										</SWRDevTools>
									</Suspense>
								)}
							</BrowserRouter>
						</SWRConfig>
						<ReactQueryDevtools initialIsOpen={false} />
					</QueryClientProvider>
				</ModalsProvider>
			</ErrorBoundary>
		</MantineProvider>
	);
}
